import { createContext, useCallback, useEffect } from 'react'
import type { FC, ReactNode } from 'react'

import { authProvider, useAuth } from 'src/auth'
import { API_URL } from 'src/platform/config'
import { log } from 'src/utils/milano'

interface ClientEventContextProps {
  children: ReactNode
}

export interface ClientEventContextValue {
  track: (event: string, properties: Record<string, unknown>) => Promise<void>
}

export const ClientEventContext = createContext<ClientEventContextValue>({
  track: async (_event, _properties) => {
    Promise.resolve()
  },
})

function logLoginSuccess(attemptKey: string) {
  const loginAttempt = localStorage.getItem(attemptKey)
  if (loginAttempt) {
    void log('loginEndOk')
    localStorage.removeItem(attemptKey)
  }
}

export const ClientEventProvider: FC<ClientEventContextProps> = (props) => {
  const { children } = props
  const { getToken, isAuthenticated, currentUser } = useAuth()

  const currentUserEmail = currentUser?.email

  // Do the milano login logging here
  useEffect(() => {
    if (isAuthenticated) {
      // dbAuth will log this inline after a successful login call,
      // not wait for the 5 seconds + quick redirect to report it to the server.
      if (authProvider !== 'dbAuth') {
        logLoginSuccess(
          `loginAttempt-${btoa((currentUserEmail ?? '') as string)}`
        )
      }
      if (authProvider === 'auth0') {
        logLoginSuccess('attemptingAuth0Login')
      }
    }
  }, [isAuthenticated, currentUserEmail])
  const track = useCallback(
    async (event: string, properties: Record<string, unknown>) => {
      if (!isAuthenticated) {
        return
      }
      // Ask the auth provider for each event so its SDK can refresh an expired
      // token. A token cached in this long-lived provider outlives the session.
      const token = await getToken()
      if (!token) return
      const time = performance.now()
      fetch(`${API_URL}/event`, {
        method: 'POST',
        headers: {
          'auth-provider': authProvider,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          event,
          properties: {
            ...properties,
            client_time_ms: time,
            client_time_iso: new Date(time).toISOString(),
          },
          userAgent: window.navigator.userAgent,
        }),
      })
    },
    [isAuthenticated, getToken]
  )
  return (
    <ClientEventContext.Provider
      value={{
        track,
      }}
    >
      {children}
    </ClientEventContext.Provider>
  )
}

export const ClientEventConsumer = ClientEventContext.Consumer
